// The app's UI language (Lingui — see lingui.config.ts).
//
// English is the source language: each message in the code is its English text, and the English catalog is
// always loaded first. That is the fallback for anything a translation hasn't reached yet, and it matters in
// a release build, where the macro keeps only message ids and English text comes from this catalog alone.
//
// Which language: the one picked in Settings (stored on this device, readable before the vault is unlocked,
// so the login screen is already in it); otherwise the first of the system's preferred languages the app
// has; otherwise English. navigator.languages is the OS language list in the desktop webview and the
// browser's list on the phone remote, so one source covers both.
//
// A change applies on the next start. t`…` reads the language when a component renders; switching live
// would need every component on Lingui's useLingui hook instead.
import { i18n, type Messages } from "@lingui/core";
import { messages as en } from "../locales/en/messages";

export { i18n };

/** A localStorage item, not a settings key (named so settingsAcl.test's key scan doesn't mistake it for one). */
const CHOICE_ITEM = "ui_locale";

export interface UiLocale {
  /** BCP-47 code, as in lingui.config.ts. */
  code: string;
  /** The language's name in that language — how people look for their own. */
  name: string;
  /** The language's ENGLISH name, for the response-language directive: a model follows "Japanese" more
   *  reliably than 日本語. Absent on dev-only locales, which must never steer what the AI writes. */
  promptName?: string;
  /** Dev builds only: a language still being translated, or the layout-test pseudo language. Drop the flag
   *  to ship it — src/lib/i18nTranslations.test.ts then requires its catalog to be complete. */
  dev?: true;
  load: () => Promise<{ messages: Messages }>;
}

/** Languages this build ships. A language moves here from DEV_ONLY when its catalog is complete —
 *  src/lib/i18nTranslations.test.ts then fails if anything in it is left untranslated. Each catalog is its
 *  own lazily-imported chunk, so a build carries them all but a run loads only the chosen one. */
const SHIPPED: readonly UiLocale[] = [
  { code: "zh", name: "简体中文", promptName: "Simplified Chinese", load: () => import("../locales/zh/messages") },
  { code: "ja", name: "日本語", promptName: "Japanese", load: () => import("../locales/ja/messages") },
  { code: "es", name: "Español", promptName: "Spanish", load: () => import("../locales/es/messages") },
  { code: "de", name: "Deutsch", promptName: "German", load: () => import("../locales/de/messages") },
  { code: "fr", name: "Français", promptName: "French", load: () => import("../locales/fr/messages") },
  { code: "ru", name: "Русский", promptName: "Russian", load: () => import("../locales/ru/messages") },
];

/** Still being translated, plus the layout-test language. Behind `import.meta.env.DEV` so a release build
 *  drops the whole list — and with it these `import()`s, which would otherwise pull every unfinished
 *  catalog into the bundle (the pseudo one alone is ~450 KB). */
const DEV_ONLY: readonly UiLocale[] = import.meta.env.DEV
  ? [
      { code: "pseudo", name: "Pseudo (layout test)", dev: true, load: () => import("../locales/pseudo/messages") },
    ]
  : [];

/** Every language besides English, shipped or not (see `dev`) — what the catalog tests check. */
export const ALL_UI_LOCALES: readonly UiLocale[] = [...SHIPPED, ...DEV_ONLY];

/** The ones this build offers in Settings. */
export const UI_LOCALES: readonly UiLocale[] = ALL_UI_LOCALES;

/**
 * The best language the app has for a list of preferred ones, walked in order: each preference matches its
 * exact tag or its base language ("es-MX" → "es"); nothing matching means English. Case-insensitive.
 *
 * When Chinese ships, decide whether Traditional ("zh-TW", "zh-HK", "zh-Hant") should fall back to English
 * rather than matching Simplified "zh" here.
 */
export function pickLocale(preferred: readonly string[], available: readonly string[]): string {
  const have = available.map((a) => a.toLowerCase());
  for (const p of preferred) {
    const tag = p.toLowerCase();
    const i = have.includes(tag) ? have.indexOf(tag) : have.indexOf(tag.split("-")[0]);
    if (i >= 0) return available[i];
  }
  return "en";
}

/**
 * The English name of the language the interface is in — what Response language falls back to when it's
 * left blank (lib/responseLanguage.ts). "" for English, whose prompts are already English, and for the
 * layout-test locale.
 */
export function uiLanguageForPrompts(): string {
  return ALL_UI_LOCALES.find((l) => l.code === i18n.locale)?.promptName ?? "";
}

/** The interface language's own name ("日本語", "Deutsch") — what to SHOW when telling someone what a blank
 *  Response language falls back to. "" when there's nothing to inherit, as in uiLanguageForPrompts. */
export function uiLanguageName(): string {
  const l = ALL_UI_LOCALES.find((x) => x.code === i18n.locale);
  return l?.promptName ? l.name : "";
}

/** The language picked in Settings, or "" to follow the system. */
export function uiLocaleChoice(): string {
  try {
    return localStorage.getItem(CHOICE_ITEM) ?? "";
  } catch {
    return ""; // storage blocked (a locked-down browser on the phone remote)
  }
}

export function setUiLocaleChoice(code: string): void {
  try {
    if (code) localStorage.setItem(CHOICE_ITEM, code);
    else localStorage.removeItem(CHOICE_ITEM);
  } catch {
    // storage blocked: the choice just won't persist
  }
}

/**
 * Locales for dates, times and numbers (Intl, toLocaleString): the translated language picked in Settings,
 * else `undefined` — the system's own formats, which already follow the OS language AND region. So an
 * English interface on a German system keeps German dates, and picking Japanese shows Japanese ones.
 */
export function intlLocales(): string[] | undefined {
  const choice = uiLocaleChoice();
  return choice && choice !== "en" && choice !== "pseudo" && UI_LOCALES.some((l) => l.code === choice) ? [choice] : undefined;
}

/** Activate the UI language. Always resolves with SOME language active, so the app never renders blank. */
export async function activateUiLocale(): Promise<void> {
  i18n.loadAndActivate({ locale: "en", messages: en });
  const codes = ["en", ...UI_LOCALES.map((l) => l.code)];
  const choice = uiLocaleChoice();
  const wanted = codes.includes(choice) ? choice : pickLocale(typeof navigator !== "undefined" ? navigator.languages ?? [] : [], codes);
  const entry = UI_LOCALES.find((l) => l.code === wanted);
  if (entry) {
    try {
      i18n.loadAndActivate({ locale: wanted, messages: (await entry.load()).messages });
    } catch (e) {
      console.warn(`[i18n] couldn't load "${wanted}", staying in English:`, e);
    }
  }
  // The document's language, so the browser picks the right fonts (Chinese and Japanese share characters but
  // draw them differently) and screen readers the right voice. Pseudo is English underneath.
  document.documentElement.lang = i18n.locale === "pseudo" ? "en" : i18n.locale;
}
